<template>
  <div class="email-verification-page">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <div class="card shadow">
            <div class="card-body text-center p-5">
              
              <!-- Loading State -->
              <div v-if="isLoading" class="loading-state">
                <div class="spinner-border text-primary mb-3" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <h3>Verifying your email...</h3>
                <p class="text-muted">Please wait while we verify your email address.</p>
              </div>

              <!-- Success State -->
              <div v-else-if="verificationStatus === 'success'" class="success-state">
                <i class="bi bi-check-circle-fill text-success mb-3" style="font-size: 4rem;"></i>
                <h3 class="text-success">Email Verified Successfully!</h3>
                <p class="text-muted mb-4">Welcome to LifeSkill! Your account has been verified.</p>
                <button @click="goToLogin" class="btn btn-primary btn-lg">
                  <i class="bi bi-box-arrow-in-right me-2"></i>
                  Go to Login
                </button>
              </div>

              <!-- Error State -->
              <div v-else-if="verificationStatus === 'error'" class="error-state">
                <i class="bi bi-exclamation-triangle-fill text-danger mb-3" style="font-size: 4rem;"></i>
                <h3 class="text-danger">Verification Failed</h3>
                <p class="text-muted mb-4">{{ errorMessage }}</p>
                <div class="d-flex gap-2 justify-content-center">
                  <button @click="goToLogin" class="btn btn-outline-primary">
                    <i class="bi bi-arrow-left me-2"></i>
                    Back to Login
                  </button>
                  <button @click="retryVerification" class="btn btn-primary">
                    <i class="bi bi-arrow-clockwise me-2"></i>
                    Try Again
                  </button>
                </div>
              </div>

              <!-- Invalid Link State -->
              <div v-else class="invalid-state">
                <i class="bi bi-link-45deg text-warning mb-3" style="font-size: 4rem;"></i>
                <h3 class="text-warning">Invalid Verification Link</h3>
                <p class="text-muted mb-4">The verification link is invalid or missing required parameters.</p>
                <button @click="goToLogin" class="btn btn-primary">
                  <i class="bi bi-arrow-left me-2"></i>
                  Back to Login
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';
import toast from '@/services/toastService';

export default {
  name: 'EmailVerificationPage',
  setup() {
    const router = useRouter();
    const route = useRoute();
    
    const isLoading = ref(false);
    const verificationStatus = ref(null);
    const errorMessage = ref('');
    const verificationToken = ref('');
    const userRole = ref('');

    // Extract token and role from URL parameters
    onMounted(() => {
      verificationToken.value = route.query.token;
      userRole.value = route.query.role;

      if (verificationToken.value && userRole.value) {
        verifyEmail();
      } else {
        verificationStatus.value = 'invalid';
      }
    });

    // Verify email with backend
    const verifyEmail = async () => {
      try {
        isLoading.value = true;
        verificationStatus.value = null;

        const API = process.env.VUE_APP_API_URL || window.location.origin
        const response = await axios.post(`${API}/api/auth/google/verify-email`, {
          token: verificationToken.value
        });

        if (response.data.success) {
          verificationStatus.value = 'success';
          toast.success('Email verified successfully! You can now sign in.');
        } else {
          throw new Error(response.data.message || 'Verification failed');
        }
      } catch (error) {
        verificationStatus.value = 'error';
        errorMessage.value = error.response?.data?.message || error.message || 'An error occurred during verification';
        toast.error(errorMessage.value);
      } finally {
        isLoading.value = false;
      }
    };

    // Retry verification
    const retryVerification = () => {
      verifyEmail();
    };

    // Navigate to login page
    const goToLogin = () => {
      router.push('/login');
    };

    return {
      isLoading,
      verificationStatus,
      errorMessage,
      verifyEmail,
      retryVerification,
      goToLogin
    };
  }
};
</script>

<style scoped>
.email-verification-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  padding: 2rem 0;
}

.card {
  border: none;
  border-radius: 20px;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.card-body {
  padding: 3rem 2rem;
}

.loading-state .spinner-border {
  width: 3rem;
  height: 3rem;
}

.success-state, .error-state, .invalid-state {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.btn {
  border-radius: 10px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-muted {
  color: #6c757d !important;
}

@media (max-width: 768px) {
  .card-body {
    padding: 2rem 1rem;
  }
}
</style> 